import { mergeAttributes, type Attributes, type JSONContent } from '@tiptap/core';
import { EmailNode } from '@react-email/editor/core';
import type { CSSProperties, ReactNode } from 'react';

/**
 * Static-image нода для @react-email/editor.
 *
 * ЗАЧЕМ: сидер email-шаблонов кладёт в БД docJson, начинающийся с
 * `image`-ноды (логотип Masters Club по URL). Готовый <EmailEditor>
 * монтируется без onUploadImage, поэтому пакет НЕ регистрирует
 * штатную image-ноду — TipTap встречает неизвестный тип `image`,
 * выкидывает `[tiptap warn] Unknown node type: image` и СБРАСЫВАЕТ
 * документ (теряется весь брендовый контент письма).
 *
 * РЕШЕНИЕ: регистрируем собственную ноду с именем `image`, схема
 * которой 1:1 совпадает с docJson из БД. Это статичная картинка по
 * URL — без файлового аплоада (никакого onUploadImage,
 * addCommands/addProseMirrorPlugins, как в штатной ноде пакета,
 * нам не нужно). Структурно повторяет штатную ноду
 * @react-email/editor (group/atom/draggable/parseHTML), плюс
 * атрибут `style` (инлайн-CSS строка), которого в штатной ноде
 * нет, но который присутствует в docJson из БД и должен пройти
 * round-trip без потерь.
 *
 * Точная схема image-ноды из БД (проверено SQL):
 *   { "type": "image", "attrs": {
 *     "alt": "Masters Club",
 *     "src": "https://platform.mastersclub.uz/images/logo-icon-email.png",
 *     "href": null,
 *     "style": "margin:0 auto;",
 *     "width": "48",
 *     "height": "48",
 *     "alignment": "center"
 *   }}
 */

/**
 * Стиль с произвольными camelCase-ключами из инлайн-CSS строки.
 * Пересечение с CSSProperties (не каст): объект остаётся
 * присваиваемым в JSX-проп `style`, при этом допускает любые
 * валидные CSS-свойства, пришедшие из docJson.
 */
type InlineStyle = CSSProperties & Record<string, string | number>;

/**
 * Преобразует инлайн-CSS строку (`margin:0 auto; color:red`) в
 * объект стилей с camelCase-ключами. Алгоритм повторяет
 * `inlineCssToJs` из бандла @react-email/editor, чтобы поведение
 * рендера совпадало со штатными нодами пакета (Hr/Heading/Section).
 */
function inlineCssToJs(inlineStyle: string | null | undefined): InlineStyle {
  const styleObject: InlineStyle = {};
  if (!inlineStyle) {
    return styleObject;
  }
  for (const declaration of inlineStyle.split(';')) {
    const trimmed = declaration.trim();
    if (!trimmed) {
      continue;
    }
    const separatorIndex = trimmed.indexOf(':');
    if (separatorIndex === -1) {
      continue;
    }
    const key = trimmed.slice(0, separatorIndex).trim();
    const value = trimmed.slice(separatorIndex + 1).trim();
    if (!key || !value) {
      continue;
    }
    const camelKey = key.replaceAll(/-\w/g, (match) => match[1].toUpperCase());
    styleObject[camelKey] = value;
  }
  return styleObject;
}

/** center-выравнивание блочной картинки в письме. */
function getAlignmentStyle(alignment: unknown): CSSProperties {
  if (alignment === 'center') {
    return { display: 'block', marginLeft: 'auto', marginRight: 'auto' };
  }
  if (alignment === 'right') {
    return { display: 'block', marginLeft: 'auto' };
  }
  if (alignment === 'left') {
    return { display: 'block', marginRight: 'auto' };
  }
  return {};
}

interface ImageNodeAttrs {
  src: string;
  alt: string;
  href: string | null;
  style: string | null;
  width: string;
  height: string;
  alignment: string;
}

function isStringAttr(value: unknown): value is string {
  return typeof value === 'string';
}

/**
 * Безопасно достаёт image-атрибуты из JSONContent без кастов:
 * каждый атрибут проверяется type guard'ом, отсутствующие
 * заменяются дефолтами схемы.
 */
function readImageAttrs(node: JSONContent): ImageNodeAttrs {
  const attrs = node.attrs ?? {};
  const href = 'href' in attrs && isStringAttr(attrs.href) ? attrs.href : null;
  const style = 'style' in attrs && isStringAttr(attrs.style) ? attrs.style : null;
  return {
    src: 'src' in attrs && isStringAttr(attrs.src) ? attrs.src : '',
    alt: 'alt' in attrs && isStringAttr(attrs.alt) ? attrs.alt : '',
    href,
    style,
    width: 'width' in attrs && isStringAttr(attrs.width) ? attrs.width : 'auto',
    height: 'height' in attrs && isStringAttr(attrs.height) ? attrs.height : 'auto',
    alignment:
      'alignment' in attrs && isStringAttr(attrs.alignment)
        ? attrs.alignment
        : 'center',
  };
}

/**
 * Кастомная нода `image` для редактора писем.
 *
 * name ОБЯЗАТЕЛЬНО `image` — совпадает с docJson из БД, иначе
 * нода не сматчится и документ снова сбросится.
 */
export const StaticImageNode = EmailNode.create({
  name: 'image',
  group: 'block',
  atom: true,
  draggable: true,

  addAttributes(): Attributes {
    return {
      src: { default: '' },
      alt: { default: '' },
      width: { default: 'auto' },
      height: { default: 'auto' },
      alignment: { default: 'center' },
      href: { default: null },
      style: {
        default: null,
        parseHTML: (element) => element.getAttribute('style'),
        renderHTML: (attributes) => {
          if (!attributes.style) {
            return {};
          }
          return { style: attributes.style };
        },
      },
    };
  },

  parseHTML() {
    return [{ tag: 'img[src]' }];
  },

  renderHTML({ HTMLAttributes }) {
    return ['img', mergeAttributes(HTMLAttributes)];
  },

  renderToReactEmail: ({ node, style }): ReactNode => {
    const attrs = readImageAttrs(node);
    const composedStyle: InlineStyle = {
      ...style,
      ...inlineCssToJs(attrs.style),
      ...getAlignmentStyle(attrs.alignment),
    };
    const img = (
      <img
        src={attrs.src}
        alt={attrs.alt}
        width={attrs.width === 'auto' ? undefined : attrs.width}
        height={attrs.height === 'auto' ? undefined : attrs.height}
        style={composedStyle}
      />
    );
    if (attrs.href) {
      return (
        <a href={attrs.href} target="_blank" rel="noreferrer">
          {img}
        </a>
      );
    }
    return img;
  },
});
